import { observer } from 'mobx-react-lite';
import React, { useEffect, useRef } from 'react';
import { HeaderSecondary } from '../typography/typography.styles';
import {
  SidebarBody,
  SidebarContainer,
  SidebarHeader,
  SidebarInner,
} from './sidebar.styles';
import { useStore } from '../../stores/store';
import { BlurredBackground } from '../blurred-background/blurred-background.styles';
import CloseButton from '../close-button/close-button.component';
import { LogoImgWrapper } from '../../features/header/header.styles';
import { useIsMobile } from '../../hooks/use-is-mobile.hook';
import { SidebarName } from '../../types/sidebar.types';
import {
  lockBodyScroll,
  unlockBodyScroll,
} from '../../utils/generic/generic.utils';
import { testIds } from '../../../test-ids';
import { useCloseOnOutsideClick } from '../../hooks/use-close-outside-click.hook';
import { useCloseOnEscape } from '../../hooks/use-close-on-escape.hook';

interface Props {
  children: React.ReactNode;
  name: SidebarName;
  header?: string | JSX.Element;
  sidebarWidth?: string;
  noBodyPadding?: boolean;
  onClose?: () => void;
  withBlurredBackground?: boolean;
  contentWithVerticalScroll?: boolean;
  withoutHeader?: boolean;
  isHeaderLogo?: boolean;
  backgroundColor?: string;
  closeOnOutsideClick?: boolean;
}

export const SIDEBAR_ZINDEX_BASE = 10000;

const Sidebar = ({
  children,
  name,
  header,
  withBlurredBackground,
  contentWithVerticalScroll,
  sidebarWidth = '48rem',
  noBodyPadding,
  withoutHeader,
  onClose,
  isHeaderLogo,
  backgroundColor,
  closeOnOutsideClick = true,
}: Props) => {
  const {
    navStore: {
      openedRightSidebars,
      isSidebarOpenedByName,
      isSidebarClosingByName,
      closeSidebarByName,
    },
  } = useStore();

  const handleClose = () => {
    if (!isSidebarOpenedByName(name)) return;

    closeSidebarByName(name);
    onClose && onClose();
  };

  const isMobile = useIsMobile();
  const isMobileRef = useRef(isMobile);
  isMobileRef.current = isMobile;

  const { popupRef } = useCloseOnOutsideClick(() => {
    if (!isMobileRef.current && closeOnOutsideClick) handleClose();
  });
  useCloseOnEscape(() => {
    if (!isMobileRef.current) handleClose();
  });
  useEffect(() => {
    if (isSidebarOpenedByName(name)) {
      lockBodyScroll();
    }

    return () => {
      if (openedRightSidebars.length === 1) {
        unlockBodyScroll();
      }
    };
  }, [isSidebarOpenedByName, name, openedRightSidebars.length]);

  if (!isSidebarOpenedByName(name) && !isSidebarClosingByName(name)) {
    return null;
  }

  const justifyContent = isMobile && isHeaderLogo ? 'center' : 'space-between';
  const padding = isMobile && isHeaderLogo ? '0.8rem 1.6rem' : '2rem 0';

  const calculateZindex = () => {
    const sidebarIndex = openedRightSidebars.indexOf(name) + 2;
    const zIndex = SIDEBAR_ZINDEX_BASE * sidebarIndex;
    return zIndex;
  };

  const ContentWrapper = contentWithVerticalScroll
    ? React.Fragment
    : SidebarInner;

  return (
    <>
      <SidebarContainer
        ref={popupRef}
        $backgroundColor={backgroundColor}
        $isOpened={isSidebarOpenedByName(name)}
        $withoutHeader={withoutHeader}
        $width={sidebarWidth}
        $zIndex={calculateZindex()}
        data-sidebar={name}
        data-testid={testIds.common.sidebar.right}>
        {!withoutHeader && (
          <SidebarHeader
            $justifyContent={justifyContent}
            $padding={padding}>
            {header && !isHeaderLogo ? (
              <>
                <HeaderSecondary>{header}</HeaderSecondary>
                <CloseButton
                  size='big'
                  onClick={(event) => {
                    event.stopPropagation();
                    handleClose();
                  }}
                />
              </>
            ) : (
              <LogoImgWrapper>{header}</LogoImgWrapper>
            )}
          </SidebarHeader>
        )}

        <SidebarBody
          $contentWithVerticalScroll={contentWithVerticalScroll}
          $padding={
            // eslint-disable-next-line
            noBodyPadding ? '0' : isMobile ? '2.4rem 1.6rem' : '2.4rem'
          }>
          <ContentWrapper>{children}</ContentWrapper>
        </SidebarBody>
      </SidebarContainer>
      {withBlurredBackground && <BlurredBackground />}
    </>
  );
};

export default observer(Sidebar);
